import React, {useCallback} from 'react';
import {useControllerProps} from '../Widget/ControllerContext';
import {
  LinkNavigationTarget,
  NavigationMessage,
  NavigationPayload,
  PreloadMessageSource,
  PreloadMessageType,
} from '../../../domain/preload/messages';

export const notifyNavigation = (payload: NavigationPayload): void => {
  const message: NavigationMessage = {
    source: PreloadMessageSource.Viewer,
    type: PreloadMessageType.Navigate,
    payload,
  };
  window.parent?.postMessage(message, '*');
};

export type NavigationLinkProps = {
  href?: string;
  role?: 'link';
  tabIndex?: number;
  onClick: (event: React.MouseEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
};

export type NavigationLinkOptions = {
  target: LinkNavigationTarget;
  url?: string;
  onClick?: (event: React.MouseEvent) => void;
};

export const useNavigationLinkProps = ({
  target,
  url,
  onClick: onLinkClick,
}: NavigationLinkOptions): NavigationLinkProps => {
  const {navigationStore} = useControllerProps();
  const shouldNotifyOnNavigate = Boolean(navigationStore?.isHeadlessPreload);

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      onLinkClick?.(event);
      if (!shouldNotifyOnNavigate) {
        return;
      }
      event.preventDefault();
      notifyNavigation({reason: target});
    },
    [shouldNotifyOnNavigate, target, onLinkClick]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }
      event.preventDefault();
      notifyNavigation({reason: target});
    },
    [target]
  );

  if (!shouldNotifyOnNavigate) {
    return {href: url, onClick};
  }
  return {role: 'link', tabIndex: 0, onClick, onKeyDown};
};
